import { memo } from 'react';

import { type AnnouncementAction, isAnnouncementCommandAction } from '../constants';
import { getAnnouncementAnchorTargetProps, useAnnouncementActionClick } from './useAnnouncementActionClick';

type AnnouncementInlineLinkProps = {
  action: AnnouncementAction;
  onAction: (action: AnnouncementAction) => void;
};

const AnnouncementInlineLink = memo(({ action, onAction }: AnnouncementInlineLinkProps) => {
  const handleClick = useAnnouncementActionClick(action, onAction);
  const content = (
    <>
      {action.label}
      <span aria-hidden="true"> →</span>
    </>
  );

  if (isAnnouncementCommandAction(action)) {
    return (
      <button
        className="ml-1 font-medium underline underline-offset-2"
        data-action-label={action.label}
        data-testid="ANNOUNCEMENT_ACTION"
        onClick={handleClick}
        type="button"
      >
        {content}
      </button>
    );
  }

  return (
    <a
      className="ml-1 font-medium underline underline-offset-2"
      data-action-label={action.label}
      data-testid="ANNOUNCEMENT_ACTION"
      href={action.url}
      onClick={handleClick}
      {...getAnnouncementAnchorTargetProps(action.url)}
    >
      {content}
    </a>
  );
});

AnnouncementInlineLink.displayName = 'AnnouncementInlineLink';

export type AnnouncementInlineLinksProps = {
  links: readonly AnnouncementAction[];
  onAction: (action: AnnouncementAction) => void;
};

export const AnnouncementInlineLinks = memo(({ links, onAction }: AnnouncementInlineLinksProps) => {
  if (links.length === 0) {
    return null;
  }

  return (
    <>
      {links.map((action, index) => (
        <AnnouncementInlineLink action={action} key={`${action.label}-${index}`} onAction={onAction} />
      ))}
    </>
  );
});

AnnouncementInlineLinks.displayName = 'AnnouncementInlineLinks';
